import { ref, computed } from "vue";
import { defineStore } from "pinia";

import { fetchUserData } from "@/api/repositories/user";
import { getReferral } from "@/api/repositories/referral";

import { resetPosthogUser } from "@/plugins/posthog";

import { resetInitialization } from "@/composables/useInitialize";

import { isVodacomTanzaniaNumber } from "@/utils/helpers.js";

import { useProfilesStore } from "./profiles";
import { useCountryStore } from "./country";
import { useProfileStore } from "./profile";
import { useDialogsStore } from "./dialogs";
import { useSurveysStore } from "./surveys";
import { usePulseStore } from "./pulse";
import { useFeatureFlagsStore } from "./featureFlags";

export const useAuthStore = defineStore(
  "auth",
  () => {
    const { resetProfiles } = useProfilesStore();
    const { resetProfile } = useProfileStore();
    const { resetSurveys } = useSurveysStore();
    const { resetDialogs } = useDialogsStore();
    const { resetCountryData } = useCountryStore();
    const { clearCache: resetPulse } = usePulseStore();
    const { resetFeatureFlags } = useFeatureFlagsStore();

    const token = ref(localStorage.getItem("token") || undefined);
    const user = ref({});
    const completeRegisterData = ref({
      phoneNumber: "",
      name: "",
      gender: "",
      birthday: "",
      lookingFor: "",
      description: "",
      location: null,
      latitude: null,
      longitude: null,
      gallery: [],
      referralData: null, // { avatar: string, name: string, code: string }
      secretCode: "", // Optional secret code entered by user
      /*
       * The attributes below are
       * used to control UI state
       */
      onboardingStep: "phoneNumber",
      initialAvatarImage: undefined,
      avatar: undefined,
      initialGalleryImage: undefined,
      galleryImage: undefined,
    });
    const isFromGhana = computed(() => {
      return user.value.country === "ghana";
    });
    const setToken = (newToken) => {
      token.value = newToken;
      localStorage.setItem("token", newToken);
    };

    const getToken = () => {
      return token.value;
    };

    const isAuthenticated = computed(() => {
      return !!token.value;
    });

    const setUser = (userData) => {
      user.value = { ...user.value, ...userData };
    };

    const resetUser = () => {
      user.value = undefined;
    };

    const resetAuth = () => {
      resetUser();
      resetToken();
      resetCompleteRegisterData();
    };

    const resetToken = () => {
      token.value = undefined;
      localStorage.removeItem("token");
    };

    const resetCompleteRegisterData = () => {
      completeRegisterData.value = {
        name: "",
        onboardingStep: "phoneNumber",
        birthday: "",
        gender: "",
        avatar: "",
        lookingFor: "",
        description: "",
        city: "",
        gallery: [],
        referralData: null,
        secretCode: "",
        initialAvatarImage: undefined,
        initialGalleryImage: undefined,
        galleryImage: undefined,
      };
    };

    const setUserPhoneIsVerified = (isVerified = true) => {
      user.value.phone_verified = isVerified;
    };

    const setUserRegisterIsCompleted = () => {
      user.value.register_completed = true;
    };

    const getUser = () => {
      return user.value;
    };

    const isUserInitialized = () => {
      return user.value !== undefined;
    };

    const isPhoneVerified = () => {
      return user.value && user.value.phone_verified;
    };

    const isRegisterCompleted = () => {
      return user.value && user.value.register_completed;
    };

    const isPushNotificationSubscribed = computed(() => {
      return user.value && user.value.push_subscribed;
    });

    const isVodacomTanzaniaUser = computed(() => {
      return user.value && isVodacomTanzaniaNumber(user.value.phone);
    });

    const isShadowBanned = computed(() => {
      return user.value && user.value._sn === true;
    });

    const getCompleteRegisterData = () => {
      return completeRegisterData.value;
    };

    const setCompleteRegisterData = (data) => {
      completeRegisterData.value = { ...completeRegisterData.value, ...data };
    };

    const logout = () => {
      resetToken();
      resetUser();
      resetProfile();
      resetCompleteRegisterData();
      resetDialogs();
      resetPosthogUser();
      resetSurveys();
      resetProfiles([]);
      resetCountryData();
      resetPulse();
      resetFeatureFlags();
      resetInitialization();
      if (window.SababuuNativeBridge?.logout) {
        window.SababuuNativeBridge.logout();
      }
    };

    const updateUserFromApi = async () => {
      const data = await fetchUserData();
      setUser(data.user);
    };

    const fetchReferralData = async (referralCode) => {
      try {
        const data = await getReferral({ referral_code: referralCode });
        completeRegisterData.value.referralData = {
          avatar: data.avatar,
          name: data.name,
          code: referralCode,
        };
        return data;
      } catch (error) {
        console.error("Failed to fetch referral data:", error);
        return null;
      }
    };

    const clearReferralData = () => {
      completeRegisterData.value.referralData = null;
    };

    const getReferralData = () => {
      return completeRegisterData.value.referralData;
    };

    return {
      setToken,
      getToken,
      isAuthenticated,
      setUser,
      resetUser,
      resetAuth,
      getUser,
      isUserInitialized,
      isPhoneVerified,
      setUserPhoneIsVerified,
      isRegisterCompleted,
      setUserRegisterIsCompleted,
      getCompleteRegisterData,
      setCompleteRegisterData,
      resetCompleteRegisterData,
      logout,
      updateUserFromApi,
      fetchReferralData,
      clearReferralData,
      getReferralData,
      token,
      user,
      completeRegisterData,
      isPushNotificationSubscribed,
      isVodacomTanzaniaUser,
      isFromGhana,
      isShadowBanned,
    };
  },
  { persist: true }
);
